import { useEffect, useRef, useState } from "react";
import { fetchCaseTypeDocuments } from "../../../api/vistaService";

// Stable per-wizard-session ref for Vista's portal gates (rate limiting,
// malware scan, cross-document checks).
export const generateSessionRef = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

/**
 * Fold a Vista crossValidation response into the findings already on screen.
 *
 * A re-run of a check supersedes its earlier result even when the
 * involved-document list differs — a blocking fail must clear once the
 * customer re-uploads the right document — while distinct same-name checks
 * within ONE response are all kept.
 *
 * A finding is only ever cleared here by ANOTHER RUN OF THE SAME CHECK. That
 * is deliberate: a response can arrive with no crossValidation at all (a
 * document-level failure, a dropped call), and wiping the board on one of
 * those would throw away findings that still stand.
 *
 * The exception is a finding that REPLACES the whole set. Vista marks it
 * `supersedesOtherChecks` — today, the government-vehicle exemption, raised
 * when the claim involves a police, civil defence, defence force, national
 * guard or Royal Court vehicle. Vista then stops running the other checks
 * entirely, so they come back ABSENT rather than present-and-passing, and
 * nothing above would clear them: a plate-coverage failure cached before the
 * police car was recognised would outlive the exemption and go on holding
 * Continue against a customer who is now exempt. So when such a finding
 * arrives, everything else goes.
 *
 * @param {Object} previous - findings keyed by checkName + involved documents
 * @param {Object[]} checks - crossValidation.checks from one Vista response
 * @param {string} [sourceSlot] - the slot whose upload produced them; the
 *   display fallback for dynamic rules we cannot attribute by name
 */
export const mergeCrossChecks = (previous, checks, sourceSlot) => {
  const supersedesEverything = (checks || []).some(
    (check) => check?.supersedesOtherChecks === true
  );
  const next = supersedesEverything ? {} : { ...previous };
  const rerun = new Set(checks.map((check) => check.checkName));
  Object.keys(next).forEach((key) => {
    if (rerun.has(next[key]?.checkName)) delete next[key];
  });
  checks.forEach((check) => {
    const key = `${check.checkName}:${(check.involvedDocuments || [])
      .map((doc) => doc.documentType)
      .sort()
      .join("|")}`;
    next[key] = { ...check, _sourceSlot: sourceSlot };
  });
  return next;
};

// Vista answers two different questions with the same set of cross-document
// findings: "here is what I make of the document you just sent"
// (validate-only) and "here is what is left after the one you took away"
// (remove-document). They are separate requests against a session both of them
// change, each takes seconds to tens of seconds — every OCR/LLM call in Vista
// shares an instance-wide concurrency limit of 2 — and nothing makes them
// answer in the order they were asked.
//
// Out of order, only the REMOVAL answer is dangerous, because it is the one
// that speaks for documents it was never given: it describes the session with
// the removed file gone, so applying it after a later read has landed wipes
// that read's findings. A customer who removes the photo a blocking check
// complained about and immediately uploads another then watches the block
// disappear, with the photos it was asking for still missing.
//
// So every answer folded into the board leaves its ticket here, and a removal
// answer is dropped once something newer has. A read's answer is never
// dropped: at worst it shows a finding about a document that has since been
// removed, which the next answer clears — the safe way round for a check that
// holds Continue.
let findingsTicketCounter = 0;
let newestFindingsApplied = 0;

/** Claim a ticket for a request that will come back with findings. */
export const takeFindingsTicket = () => (findingsTicketCounter += 1);

/** Record that this answer's findings are the ones now on the board. */
export const noteFindingsApplied = (ticket) => {
  newestFindingsApplied = Math.max(newestFindingsApplied, ticket);
};

/** Has a newer answer already been applied over this one? */
export const findingsAnswerOvertaken = (ticket) =>
  ticket <= newestFindingsApplied;

/**
 * Take the session handle Vista minted for a file it has just read.
 *
 * Every validate-only response carries `sessionFiles`, the whole session's
 * inventory: one { documentType, fileId } per file it holds, in upload order
 * within each slot. There is nothing in an entry to match a file by — no name,
 * no size — so the file just sent takes the first entry of its own
 * documentType that no file on screen is already holding.
 *
 * `claimed` is the page's set of handles already spoken for, and must be
 * seeded from files restored out of redux, or a new upload takes an entry that
 * belongs to a file already on the page.
 *
 * The pairing is only as good as the order Vista added the files in: several
 * photos dropped in at once are read concurrently, and two entries of the same
 * documentType are indistinguishable. Echoing the file's name, or a reference
 * the client sends, in `sessionFiles` would make it exact — worth asking for
 * before this is leaned on harder.
 *
 * The handle is stamped on the file object, alongside the other _vista* fields,
 * so it survives the redux round trip when the customer navigates Back. It is
 * session-scoped and does not outlive a Vista restart, which is fine: it is
 * only ever read to remove the file it belongs to, in this same session.
 *
 * @param {File} file - the file whose validate-only call just returned
 * @param {string} documentType - the Vista label it was sent under
 * @param {{documentType: string, fileId: string}[]} sessionFiles
 * @param {Set<string>} claimed - handles already held by files on the page
 */
export const claimSessionFileId = (file, documentType, sessionFiles, claimed) => {
  if (!file || !Array.isArray(sessionFiles)) return;
  const entry = sessionFiles.find(
    (item) =>
      item?.documentType === documentType &&
      item?.fileId &&
      !claimed.has(item.fileId)
  );
  if (!entry) return;
  claimed.add(entry.fileId);
  file._vistaFileId = entry.fileId;
};

/**
 * The wizard's Vista session: the sessionRef every validate-only call rides
 * on, and the cross-document findings accumulated under it.
 *
 * One ref for the whole wizard, from first upload to the mode=full submit —
 * that is what lets Vista cross-check a document against the ones already
 * uploaded, and what lets the submit drop the session's PII cache.
 *
 * Removing a document is a server-side matter: the session holds every
 * document Vista has read, so the upload pages take a removed file out of it
 * with removeSessionDocument and re-render the checks Vista returns with it.
 *
 * It also carries the case type's document config — how many files each slot
 * accepts — because that is the other thing every upload page needs and the
 * other thing that must be fetched exactly once.
 */
const useVistaSession = () => {
  const sessionRef = useRef(generateSessionRef()).current;
  const [crossChecks, setCrossChecks] = useState({});
  // How many files each document slot takes, straight from the case type's
  // Vista config. Fetched once for the whole wizard rather than per upload
  // page: the pages are keyed by substep and remount on every step, and the
  // limits are the same set of numbers for all of them.
  //
  // null until it answers, and null forever if it does not — the upload pages
  // read that as "use the built-in ceiling", so a customer is never held up by
  // a config call.
  const [documentLimits, setDocumentLimits] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchCaseTypeDocuments().then((limits) => {
      if (!cancelled && limits) setDocumentLimits(limits);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { sessionRef, crossChecks, setCrossChecks, documentLimits };
};

export default useVistaSession;
